import { User } from '@auth0/auth0-react'
import { deleteRequestStatus, postRequest, postRequestStatus } from 'features/common'
import { Connection, ProviderType } from 'features/connections'
import { ConnectionType } from 'features/data-explorer/types'
import { sendSQLPostRequest } from 'features/sql/api/sql'
import settings from 'settings'
import { DatasetLicense, TableConnectionProp } from '../types'
import { getClient } from 'features/common/utils/clientParameter'
import { SQLResponse } from 'features/sql/types'
import { cleanChar } from 'features/common/utils/strings'

export const deleteSubscription = async (id: string) => {
  const url = `${settings.accountsApiUrl}/do/subscriptions/${id}`
  const response = await deleteRequestStatus(url)

  if (response === 409) {
    throw 'Cannot subscribe'
  }
}

export const subscribePublicData = async (source: string) => {
  const url = `${settings.accountsApiUrl}/do/subscriptions`
  const response = await postRequestStatus(url, {
    type: 'public',
    source
  })

  if (response !== 201) {
    throw 'Cannot subscribe'
  }
}

export const subscribeSample = async (source: string) => {
  const url = `${settings.accountsApiUrl}/do/subscriptions`
  const response = await postRequestStatus(url, {
    type: 'sample',
    source
  })

  if (response !== 201) {
    throw 'Cannot subscribe'
  }
}

export const getSubscriptionsDatasets = async ({
  connection = ConnectionType.CartoDw,
  project,
  dataProject,
  dataset
}: {
  connection: string
  project: string
  dataProject: string
  dataset: string
}) => {
  return await sendSQLPostRequest({
    connection: connection,
    query: `
      WITH user_subs AS (
        SELECT table_name AS sub_name
        FROM \`${dataProject}.${dataset}.INFORMATION_SCHEMA.TABLES\`
        WHERE \`${project}.carto.__HAS_SUB_PREFIX\`(table_name)
      ),
      result AS (
        SELECT
            d.dataset_slug,
            d.dataset_name,
            d.dataset_country,
            d.dataset_category,
            CASE d.dataset_is_public
            WHEN TRUE THEN
              '${DatasetLicense.PUBLIC}'
            ELSE
              '${DatasetLicense.PREMIUM}'
            END AS dataset_license,
            d.dataset_provider,
            d.dataset_version,
            d.dataset_geom_type AS dataset_geo_type,
            us.sub_name AS dataset_table,
            COALESCE(gus.sub_name, d.associated_geography_id) AS associated_geography_table,
            g.dataset_slug AS associated_geography_slug
        FROM user_subs us
        INNER JOIN \`${project}.carto.spatial_catalog_datasets\` d
          ON (REPLACE(SUBSTRING(d.dataset_id, INSTR(d.dataset_id, '.')+1), '.', '_') = \`${project}.carto.__REMOVE_SUB_PREFIX\`(us.sub_name))
        LEFT OUTER JOIN user_subs gus
          ON (REPLACE(SUBSTRING(d.associated_geography_id, INSTR(d.associated_geography_id, '.')+1), '.', '_') = \`${project}.carto.__REMOVE_SUB_PREFIX\`(gus.sub_name))
        LEFT OUTER JOIN \`${project}.carto.spatial_catalog_datasets\` g
          ON (d.associated_geography_id = g.dataset_id)
        WHERE d.dataset_is_product
      )
      SELECT * FROM result ORDER BY dataset_slug`,
    extraParams: { client: getClient() }
  })
}

export const getSubscriptionsDatasetsWithSpatialIndex = async ({
  connection = ConnectionType.CartoDw,
  project,
  dataProject,
  dataset
}: {
  connection: string
  project: string
  dataProject: string
  dataset: string
}) => {
  return await sendSQLPostRequest({
    connection: connection,
    query: `
      WITH user_subs AS (
        SELECT table_name AS sub_name
        FROM \`${dataProject}.${dataset}.INFORMATION_SCHEMA.TABLES\`
        WHERE \`${project}.carto.__HAS_SUB_PREFIX\`(table_name)
      ),
      result AS (
        SELECT
            d.dataset_slug,
            d.dataset_name,
            d.dataset_country,
            d.dataset_category,
            CASE d.dataset_is_public
            WHEN TRUE THEN
              '${DatasetLicense.PUBLIC}'
            ELSE
              '${DatasetLicense.PREMIUM}'
            END AS dataset_license,
            d.dataset_provider,
            d.dataset_version,
            d.dataset_geom_type AS dataset_geo_type,
            us.sub_name AS dataset_table,
            COALESCE(gus.sub_name, d.associated_geography_id) AS associated_geography_table,
            g.dataset_slug AS associated_geography_slug,
            d.spatial_index,
            d.spatial_index_resolution
        FROM user_subs us
        INNER JOIN \`${project}.carto.spatial_catalog_datasets\` d
          ON (REPLACE(SUBSTRING(d.dataset_id, INSTR(d.dataset_id, '.')+1), '.', '_') = \`${project}.carto.__REMOVE_SUB_PREFIX\`(us.sub_name))
        LEFT OUTER JOIN user_subs gus
          ON (REPLACE(SUBSTRING(d.associated_geography_id, INSTR(d.associated_geography_id, '.')+1), '.', '_') = \`${project}.carto.__REMOVE_SUB_PREFIX\`(gus.sub_name))
        LEFT OUTER JOIN \`${project}.carto.spatial_catalog_datasets\` g
          ON (d.associated_geography_id = g.dataset_id)
        WHERE d.dataset_is_product
      )
      SELECT * FROM result ORDER BY dataset_slug`,
    extraParams: {
      client: getClient()
    }
  })
}

export const getSamplesDatasets = async ({
  connection = ConnectionType.CartoDw,
  project,
  dataProject,
  dataset
}: {
  connection: string
  project: string
  dataProject: string
  dataset: string
}) => {
  return await sendSQLPostRequest({
    connection: connection,
    query: `
      WITH user_samples AS (
        SELECT table_name AS sample_name
        FROM \`${dataProject}.${dataset}.INFORMATION_SCHEMA.TABLES\`
        WHERE \`${project}.carto.__HAS_SAMPLE_PREFIX\`(table_name)
      ),
      result AS (
        SELECT
            d.dataset_slug,
            d.dataset_name,
            d.dataset_country,
            d.dataset_category,
            CASE d.dataset_is_public
            WHEN TRUE THEN
              '${DatasetLicense.PUBLIC}'
            ELSE
              '${DatasetLicense.PREMIUM}'
            END AS dataset_license,
            d.dataset_provider,
            d.dataset_version,
            d.dataset_geom_type AS dataset_geo_type,
            us.sample_name AS dataset_table
        FROM user_samples us
        INNER JOIN \`${project}.carto.spatial_catalog_datasets\` d
          ON (REPLACE(SUBSTRING(d.dataset_id, INSTR(d.dataset_id, '.')+1), '.', '_') = \`${project}.carto.__REMOVE_SAMPLE_PREFIX\`(us.sample_name))
      )
      SELECT * FROM result  ORDER BY dataset_slug`,
    extraParams: { client: getClient() }
  })
}

export const trackRequestSubscriptionRequest = async ({
  user,
  datasetId
}: {
  user: User
  datasetId: string
}): Promise<void> => {
  const { workspaceApiUrl } = settings

  const metricsEndpointUrl = `${workspaceApiUrl}/events`
  await postRequest(metricsEndpointUrl, {
    event_type: 'DOSubscriptionRequested',
    payload: {
      accountId: user.user_metadata.account_id,
      userId: user.user_id,
      datasetId: datasetId
    },
    timestamp: new Date()
  })
}

export const searchDOTableInConnection = async ({ connection, table }: TableConnectionProp) => {
  try {
    const tableName = connection.provider_id === ProviderType.POSTGRES && table.slug ? `sub_${table.slug}` : table.id
    return await sendSQLPostRequest({
      connection: connection.name,
      ...getSearchDOTableQuery(tableName, connection),
      extraParams: { client: getClient() }
    })
  } catch (e) {
    return false
  }
}

const getSearchDOTableQuery = (table: string, connection: Connection) => {
  switch (connection.provider_id) {
    case ProviderType.SNOWFLAKE:
      return {
        query: `
        select system$explain_plan_json(
          'select * from ${connection.doLocation}.${table}'
        ) as explain_plan;
      `
      }
    case ProviderType.REDSHIFT:
    case ProviderType.POSTGRES:
      return {
        query: `
        explain select * from ${connection.doLocation}.${table};
      `
      }
    case ProviderType.BIGQUERY:
      return {
        query: `
          SELECT * FROM \`${cleanChar(connection.doLocation, '`')}\`.${table} LIMIT 0
        `
      }
    default:
      throw new Error(`Provider ${connection.provider_id} is not supported yet`)
  }
}

export const listTablesInConnection = async ({
  connection
}: {
  connection: Connection
}): Promise<string[] | undefined> => {
  try {
    if (connection.carto_dw) {
      // Skip the query
      return undefined
    }
    const response = await sendSQLPostRequest({
      connection: connection.name,
      query: getListTablesQuery(connection),
      extraParams: { client: getClient() }
    })
    return processListTablesResponse(response, connection)
  } catch (e) {
    return []
  }
}

const getListTablesQuery = (connection: Connection) => {
  const doLocation = connection.doLocation
  const database = doLocation.includes('.') ? doLocation.split('.')[0] : `"${connection.config.credentials.database}"`
  const schema = doLocation.includes('.') ? doLocation.split('.')[1] : doLocation
  switch (connection.provider_id) {
    case ProviderType.BIGQUERY:
      return `
        SELECT table_name
        FROM \`${cleanChar(doLocation, '`')}\`.INFORMATION_SCHEMA.TABLES
        WHERE table_name LIKE 'sub_%' OR table_name LIKE 'sample_%'
      `
    case ProviderType.SNOWFLAKE:
      return `
        SELECT TABLE_NAME
        FROM ${database}.INFORMATION_SCHEMA.TABLES
        WHERE TABLE_SCHEMA = '${schema.includes('"') ? cleanChar(schema, '"') : schema.toUpperCase()}' AND
          (TABLE_NAME LIKE 'SUB_%' OR TABLE_NAME LIKE 'SAMPLE_%')
      `
    case ProviderType.REDSHIFT:
    case ProviderType.POSTGRES:
      return `
        SELECT table_name
        FROM ${database}.INFORMATION_SCHEMA.TABLES
        WHERE table_schema = '${schema.includes('"') ? cleanChar(schema, '"') : schema.toLowerCase()}' AND
          (table_name LIKE 'sub_%' OR table_name LIKE 'sample_%')
      `
    default:
      throw new Error(`Provider ${connection.provider_id} is not supported yet`)
  }
}

const processListTablesResponse = (response: SQLResponse | undefined, connection: Connection): string[] | undefined => {
  switch (connection.provider_id) {
    case ProviderType.SNOWFLAKE:
      return response?.rows.map((t) => (t.TABLE_NAME as string).toLowerCase())
    case ProviderType.BIGQUERY:
    case ProviderType.REDSHIFT:
    case ProviderType.POSTGRES:
      return response?.rows.map((t) => t.table_name)
    default:
      throw new Error(`Provider ${connection.provider_id} is not supported yet`)
  }
}

export const getProvidersMapping = async () => {
  const url = `${settings.spatialDataCatalogUrl}/do/catalog/providers?limit=0`
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch providers mapping: ${response.status} ${response.statusText}`)
  }
  return await response.json()
}
